import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  CreditCard, RefreshCw, Search, Filter, DollarSign, Loader2,
  AlertCircle, CheckCircle2, Clock, Ban
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VendorOpenItems = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Summary stats
  const [summary, setSummary] = useState({
    total: 0,
    totalAmount: 0,
    overdueCount: 0,
    overdueAmount: 0
  });

  const calculateSummary = useCallback((data) => {
    const today = new Date();
    let overdueCount = 0;
    let overdueAmount = 0;
    let totalAmount = 0;

    data.forEach(item => {
      totalAmount += item.open_amount;
      const dueDate = new Date(item.due_date);
      if (dueDate < today && item.status !== 'PAID') {
        overdueCount++;
        overdueAmount += item.open_amount;
      }
    });

    setSummary({
      total: data.length,
      totalAmount,
      overdueCount,
      overdueAmount
    });
  }, []);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/ap/open-items?include_blocked=true`);
      setItems(response.data);
      calculateSummary(response.data);
    } catch (error) {
      console.error('Error loading open items:', error);
      toast.error('Failed to load vendor open items');
    } finally {
      setLoading(false);
    }
  }, [calculateSummary]);

  const filterItems = useCallback(() => {
    let filtered = [...items];

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(i => i.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(i =>
        i.vendor_id?.toLowerCase().includes(term) ||
        i.vendor_name?.toLowerCase().includes(term) ||
        i.invoice_document?.toLowerCase().includes(term) ||
        i.po_number?.toLowerCase().includes(term)
      );
    }

    setFilteredItems(filtered);
  }, [items, statusFilter, searchTerm]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    filterItems();
  }, [filterItems]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(`${BACKEND_URL}/api/ap/open-items/sync`);
      toast.success(response.data.message);
      loadItems();
    } catch (error) {
      toast.error('Failed to sync open items');
    } finally {
      setSyncing(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'OPEN': 'bg-orange-100 text-orange-800',
      'PARTIALLY_PAID': 'bg-yellow-100 text-yellow-800',
      'PAID': 'bg-green-100 text-green-800',
      'BLOCKED': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'OPEN': return <Clock className="w-4 h-4" />;
      case 'PAID': return <CheckCircle2 className="w-4 h-4" />;
      case 'BLOCKED': return <Ban className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="vendor-open-items">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <CreditCard className="w-7 h-7 text-orange-600" />
              Vendor Open Items
            </h1>
            <p className="text-sm text-gray-500">Accounts Payable - Outstanding vendor invoices</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSync} disabled={syncing}>
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sync from Invoices
            </Button>
            <Button onClick={() => navigate('/ap/payment-proposal')} className="bg-emerald-600 hover:bg-emerald-700">
              <DollarSign className="w-4 h-4 mr-2" />
              Payment Run (F110)
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Open Items</p>
                <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
              </div>
              <CreditCard className="w-8 h-8 text-orange-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Payables</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(summary.totalAmount)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-orange-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overdue Items</p>
                <p className="text-2xl font-bold text-red-600">{summary.overdueCount}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overdue Amount</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.overdueAmount)}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by vendor, invoice, PO..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="ALL">All Status</option>
                <option value="OPEN">Open</option>
                <option value="PARTIALLY_PAID">Partially Paid</option>
                <option value="BLOCKED">Blocked</option>
              </select>
            </div>
            <Button variant="outline" size="sm" onClick={loadItems}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white rounded-lg shadow-sm border">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading open items...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-8 text-center">
              <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600">No open items found</p>
              <p className="text-sm text-gray-500 mt-2">Sync from posted invoices to populate open items</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Reference</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Original Amt</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Open Amt</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredItems.map((item) => (
                    <tr key={item.item_id} className={`hover:bg-gray-50 ${isOverdue(item.due_date) && item.status === 'OPEN' ? 'bg-red-50' : ''}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{item.vendor_id}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[150px]">{item.vendor_name}</p>
                      </td>
                      <td className="px-4 py-3 font-medium text-orange-600">{item.invoice_document}</td>
                      <td className="px-4 py-3 text-gray-600">{item.po_number || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{item.invoice_date}</td>
                      <td className="px-4 py-3">
                        <span className={isOverdue(item.due_date) && item.status === 'OPEN' ? 'text-red-600 font-medium' : 'text-gray-600'}>
                          {item.due_date}
                        </span>
                        {isOverdue(item.due_date) && item.status === 'OPEN' && (
                          <span className="ml-2 text-xs text-red-600">(Overdue)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatCurrency(item.original_amount, item.currency)}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-orange-700">
                        {formatCurrency(item.open_amount, item.currency)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                          {getStatusIcon(item.status)}
                          {item.status}
                        </span>
                        {item.payment_block && (
                          <span className="ml-1 text-xs text-red-600">({item.payment_block})</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="mt-4 text-sm text-gray-500">
          Showing {filteredItems.length} of {items.length} items
        </div>
      </div>
    </Layout>
  );
};

export default VendorOpenItems;
